import { Injectable, inject } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { IThumbnailProvider } from '@3ddv/software-division-components';
import { DvmService } from './dvm.service';
import { PopoverDataTransformerService } from './popover-data-transformer.service';

/**
 * GroupSales-specific implementation of IThumbnailProvider.
 * This service provides thumbnails using the existing DvmService from GroupSales.
 */
@Injectable({
  providedIn: 'root',
})
export class GroupSalesThumbnailProviderService implements IThumbnailProvider {
  private dvmService = inject(DvmService);
  private dataTransformer = inject(PopoverDataTransformerService);
  private loaded = new Map<string, string>();

  /**
   * The thumbnail URL of a node that was already loaded, or undefined.
   * A popover that is shown again (for example to refresh its Select label) gets it at once
   * and does not show the loading spinner again.
   */
  peekThumbnail(nodeId: string): string | undefined {
    return this.loaded.get(nodeId);
  }

  /**
   * Gets a thumbnail for the specified node ID.
   * @param nodeId - The node ID to get thumbnail for
   * @returns Observable of thumbnail URL
   */
  getThumbnail(nodeId: string): Observable<string> {
    // Use the data transformer to get the appropriate thumbnail section ID
    const thumbnailSectionId =
      this.dataTransformer.getThumbnailSectionId(nodeId);

    // Load thumbnail using existing DvmService
    return this.dvmService.loadThumbnail(thumbnailSectionId).pipe(
      map((img: HTMLImageElement) => img.src),
      tap(url => this.loaded.set(nodeId, url))
    );
  }
}
